import { ElementType, useState } from "react";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import AutoStoriesRoundedIcon from "@mui/icons-material/AutoStoriesRounded";
import ChildCareRoundedIcon from "@mui/icons-material/ChildCareRounded";
import CoffeeRoundedIcon from "@mui/icons-material/CoffeeRounded";
import FitnessCenterRoundedIcon from "@mui/icons-material/FitnessCenterRounded";
import LocalCafeRoundedIcon from "@mui/icons-material/LocalCafeRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import SportsGolfRoundedIcon from "@mui/icons-material/SportsGolfRounded";
import SportsSoccerRoundedIcon from "@mui/icons-material/SportsSoccerRounded";
import SportsTennisRoundedIcon from "@mui/icons-material/SportsTennisRounded";
import TvRoundedIcon from "@mui/icons-material/TvRounded";

import { hpointAssets } from "../../design-system/hpoint/assets";
import { userState } from "../../interface/MainInterface";
import "./CommunityInfo.css";

interface PropsType {
  userState: userState;
}

type ServiceTone = "purple" | "blue" | "mint" | "yellow" | "orange";
type Audience = "전체" | "유아" | "어린이" | "청소년" | "성인" | "시니어";
type ZoneId = "play" | "active" | "rest";
type DestinationStatus = "오픈예정";

interface ServiceGuide {
  name: string;
  icon: ElementType;
  hours: string;
  price: string;
  regularPrice?: string;
  audience?: string;
  note: string;
  tone: ServiceTone;
}

interface DestinationGuide {
  name: string;
  icon: ElementType;
  summary: string;
  status?: DestinationStatus;
  recommendation: string;
  audiences: Exclude<Audience, "전체">[];
  zone: ZoneId;
  tone: ServiceTone;
}

const audienceFilters: Audience[] = ["전체", "유아", "어린이", "청소년", "성인", "시니어"];

const discoveryZones: Array<{
  id: ZoneId;
  label: string;
  title: string;
  description: string;
}> = [
  {
    id: "play",
    label: "PLAY PARK",
    title: "신나게 놀아요",
    description: "호기심을 펼치고 친구들과 함께 신나게 뛰어노는 공간이에요.",
  },
  {
    id: "active",
    label: "ACTIVE PARK",
    title: "건강하게 움직여요",
    description: "내 속도에 맞는 운동으로 일상에 활력을 채워보세요.",
  },
  {
    id: "rest",
    label: "REST & STUDY PARK",
    title: "쉬고 몰입해요",
    description: "책과 배움, 편안한 대화가 머무는 차분한 공간이에요.",
  },
];

const destinationGuides: DestinationGuide[] = [
  {
    name: "키즈룸",
    icon: ChildCareRoundedIcon,
    summary: "우리 아이 놀이 아지트",
    recommendation: "유아 추천",
    audiences: ["유아"],
    zone: "play",
    tone: "yellow",
  },
  {
    name: "놀이터",
    icon: ChildCareRoundedIcon,
    summary: "집 가까이에서 활발하게 뛰노는 야외 놀이 공간",
    recommendation: "유아·어린이 추천",
    audiences: ["유아", "어린이"],
    zone: "play",
    tone: "orange",
  },
  {
    name: "풋살장",
    icon: SportsSoccerRoundedIcon,
    summary: "친구들과 시원하게 달리는 팀 플레이 존",
    recommendation: "어린이·청소년 추천",
    audiences: ["어린이", "청소년"],
    zone: "play",
    tone: "mint",
  },
  {
    name: "GX룸",
    icon: FitnessCenterRoundedIcon,
    summary: "음악과 함께 활력을 채우는 그룹 운동 공간 #필라테스",
    recommendation: "청소년·성인 추천",
    audiences: ["청소년", "성인"],
    zone: "active",
    tone: "purple",
  },
  {
    name: "헬스장",
    icon: FitnessCenterRoundedIcon,
    summary: "내 속도대로 꾸준히 움직이는 데일리 피트니스",
    recommendation: "청소년부터 시니어까지 추천",
    audiences: ["청소년", "성인", "시니어"],
    zone: "active",
    tone: "blue",
  },
  {
    name: "골프연습장",
    icon: SportsGolfRoundedIcon,
    summary: "전 타석 스크린, 스윙 감각을 다듬는 연습 공간",
    recommendation: "성인 추천",
    audiences: ["성인"],
    zone: "active",
    tone: "mint",
  },
  {
    name: "탁구장",
    icon: SportsTennisRoundedIcon,
    summary: "이웃과 함께 즐겁게 운동하는 탁구 공간",
    recommendation: "청소년·성인·시니어 추천",
    audiences: ["청소년", "성인", "시니어"],
    zone: "active",
    tone: "orange",
  },
  {
    name: "북카페",
    icon: LocalCafeRoundedIcon,
    summary: "엔터테이먼트가 한곳에, 편안한 라운지",
    recommendation: "전체 추천",
    audiences: ["유아", "어린이", "청소년", "성인", "시니어"],
    zone: "rest",
    tone: "orange",
  },
  {
    name: "작은 도서관",
    icon: AutoStoriesRoundedIcon,
    summary: "매월 최신 도서 업데이트",
    recommendation: "유아부터 성인까지 추천",
    audiences: ["유아", "어린이", "청소년", "성인"],
    zone: "rest",
    tone: "blue",
  },
  {
    name: "독서실",
    icon: MenuBookRoundedIcon,
    summary: "공부와 몰입이 필요한 순간을 위한 조용한 자리",
    recommendation: "청소년 추천",
    audiences: ["청소년"],
    zone: "rest",
    tone: "yellow",
  },
  {
    name: "경로당",
    icon: CoffeeRoundedIcon,
    summary: "이웃과 담소를 나누며 편안히 쉬는 사랑방",
    status: "오픈예정",
    recommendation: "시니어 추천",
    audiences: ["시니어"],
    zone: "rest",
    tone: "purple",
  },
];

const recommendationCourses = [
  {
    kicker: "아이와 신나는 오후",
    description: "놀이와 책, 달콤한 휴식을 한 번에",
    route: ["놀이터", "작은 도서관", "북카페"],
  },
  {
    kicker: "방과 후 에너지 충전",
    description: "친구들과 움직인 뒤 차분하게 몰입하기",
    route: ["풋살장", "작은 도서관", "독서실"],
  },
  {
    kicker: "나를 위한 운동과 휴식",
    description: "오늘의 컨디션에 맞춰 활력 채우기",
    route: ["GX룸", "헬스장", "북카페"],
  },
  {
    kicker: "가볍고 편안한 하루",
    description: "천천히 몸을 움직이고 이웃과 쉬어가기",
    route: ["헬스장", "경로당"],
  },
];

const paidServices: ServiceGuide[] = [
  {
    name: "골프연습장",
    icon: SportsGolfRoundedIcon,
    hours: "05:00 - 23:00",
    price: "월 20,000원",
    audience: "만 13세 이상",
    note: "월 이용권을 신청한 뒤 원하는 타석과 시간을 예약해 이용해 주시기 바랍니다.",
    tone: "mint",
  },
  {
    name: "스크린 게임",
    icon: TvRoundedIcon,
    hours: "05:00 - 23:00",
    price: "9홀 2,000원 · 18홀 4,000원",
    audience: "만 13세 이상",
    note: "이용 당일까지 예약할 수 있습니다. 인원과 홀 수에 맞는 예약 시간을 확인해 주시기 바랍니다.",
    tone: "purple",
  },
  {
    name: "헬스장",
    icon: FitnessCenterRoundedIcon,
    hours: "05:00 - 23:00",
    price: "월 5,000원",
    audience: "만 13세 이상",
    note: "헬스장 전용 신발을 착용한 뒤 이용 가능합니다.",
    tone: "blue",
  },
  {
    name: "독서실",
    icon: MenuBookRoundedIcon,
    hours: "06:00 - 익일 02:00",
    price: "월 20,000원",
    audience: "만 13세 이상",
    note: "냉난방기와 공기청정기가 준비되어 있어 쾌적하게 이용하실 수 있습니다.",
    tone: "yellow",
  },
  {
    name: "북카페",
    icon: LocalCafeRoundedIcon,
    hours: "10:00 - 22:00",
    price: "1시간 5,000원 (할인)",
    regularPrice: "10,000원",
    audience: "만 13세 이하는 보호자 동반",
    note: "한 번의 예약은 최대 5시간이며 반드시 연속으로 선택해야 합니다. 배달 음식과 주류는 허용하지만 조리는 금지하며, 생일 파티 때는 화재 예방 수칙을 지켜 주세요. 빔프로젝터에서는 개인 OTT 계정을 사용하고 이용 후 반드시 로그아웃해야 합니다. 반려동물 동반 금지. 퇴실 전 정리하고, 시설 오염이나 파손이 있으면 관리실에 알려주세요.",
    tone: "orange",
  },
  {
    name: "탁구장",
    icon: SportsTennisRoundedIcon,
    hours: "10:00 - 22:00",
    price: "1시간 2,000원",
    audience: "청소년·성인·시니어",
    note: "1시간 단위로 한 번에 최대 3시간까지 연속 예약할 수 있습니다.",
    tone: "orange",
  },
  {
    name: "락커",
    icon: LockRoundedIcon,
    hours: "각 시설 운영시간",
    price: "골프연습장 월 5,000원 · 헬스장 월 3,000원",
    audience: "만 13세 이상",
    note: "이용 기간이 끝나기 전에 개인 물품을 정리해 다음 이용자를 배려해 주시기 바랍니다.",
    tone: "orange",
  },
];

const freeServices: ServiceGuide[] = [
  {
    name: "키즈룸",
    icon: ChildCareRoundedIcon,
    hours: "10:00 - 19:00",
    price: "무료",
    audience: "18개월 이상 - 만 4세 이하",
    note: "어린이를 위한 공간입니다. 연령을 확인하고 안전하게 이용해 주시기 바랍니다.",
    tone: "yellow",
  },
  {
    name: "작은 도서관",
    icon: AutoStoriesRoundedIcon,
    hours: "06:00 - 익일 02:00",
    price: "무료",
    audience: "만 5세 이상",
    note: "약 1,800권의 도서가 구비되어 있으며, 매월 베스트셀러를 구매해 비치하고 있습니다.",
    tone: "blue",
  },
  {
    name: "풋살장 · 놀이터",
    icon: SportsSoccerRoundedIcon,
    hours: "하절기 07:00 - 20:00 · 동절기 07:00 - 19:00",
    price: "무료",
    audience: "만 5세 이상",
    note: "계절에 따라 운영시간이 다릅니다.",
    tone: "mint",
  },
  {
    name: "맘카페",
    icon: CoffeeRoundedIcon,
    hours: "10:00 - 17:00",
    price: "무료",
    note: "지상 맘카페는 오후 5시까지 운영합니다.",
    tone: "purple",
  },
];

const ServiceCard = ({ service }: { service: ServiceGuide }) => {
  const ServiceIcon = service.icon;

  return (
    <article
      className={`hp-community-info__service-card hp-community-info__service-card--${service.tone}`}
      aria-label={service.name}
    >
      <div className="hp-community-info__service-heading">
        <span className="hp-community-info__service-icon" aria-hidden="true">
          <ServiceIcon />
        </span>
        <div>
          <h3>{service.name}</h3>
          <span className="hp-community-info__price-badge">{service.price === "무료" ? "무료 이용" : "유료 시설"}</span>
        </div>
      </div>

      <dl className="hp-community-info__details">
        <div>
          <dt>운영시간</dt>
          <dd>{service.hours}</dd>
        </div>
        <div>
          <dt>이용요금</dt>
          <dd className={service.regularPrice ? "hp-community-info__discounted-price" : undefined}>
            {service.regularPrice ? (
              <>
                <del className="hp-community-info__regular-price">{service.regularPrice}</del>
                <strong className="hp-community-info__discount-price">{service.price}</strong>
              </>
            ) : service.price}
          </dd>
        </div>
        {service.audience ? (
          <div>
            <dt>이용대상</dt>
            <dd>{service.audience.startsWith("만 13세 이하") ? <strong className="hp-book-cafe-guardian-notice">{service.audience}</strong> : service.audience}</dd>
          </div>
        ) : null}
      </dl>

      <div className="hp-community-info__service-note">
        <strong>이용 전 확인</strong>
        <p>{service.note}</p>
      </div>
    </article>
  );
};

const ServiceSection = ({
  id,
  title,
  description,
  services,
}: {
  id: string;
  title: string;
  description?: string;
  services: ServiceGuide[];
}) => (
  <section className="hp-community-info__section" aria-labelledby={id}>
    <header className="hp-community-info__section-heading">
      <h2 id={id}>{title}</h2>
      {description ? <p>{description}</p> : null}
    </header>
    <div className="hp-community-info__service-grid">
      {services.map((service) => (
        <ServiceCard key={service.name} service={service} />
      ))}
    </div>
  </section>
);

const DestinationCard = ({ destination }: { destination: DestinationGuide }) => {
  const DestinationIcon = destination.icon;

  return (
    <article
      className={`hp-community-info__destination-card hp-community-info__destination-card--${destination.tone}`}
      aria-label={destination.name}
    >
      <span className="hp-community-info__destination-icon" aria-hidden="true">
        <DestinationIcon />
      </span>
      <div className="hp-community-info__destination-title">
        <h3>{destination.name}</h3>
        {destination.status ? (
          <span className="hp-community-info__destination-status hp-community-info__destination-status--pending">
            {destination.status}
          </span>
        ) : null}
      </div>
      <p className="hp-community-info__destination-summary">{destination.summary}</p>
      <div className="hp-community-info__recommendation-badges" aria-label="추천 대상">
        <span className="hp-community-info__recommendation-badge">{destination.recommendation}</span>
      </div>
    </article>
  );
};

const CommunityInfo = (_props: PropsType) => {
  const [selectedAudience, setSelectedAudience] = useState<Audience>("전체");
  const filteredDestinations = destinationGuides.filter(
    (destination) => selectedAudience === "전체" || destination.audiences.includes(selectedAudience),
  );

  return (
    <main className="hpoint-renewal hp-community-info">
      <section className="hp-community-info__park-experience" aria-label="커뮤니티 센터">
        <header className="hp-community-info__park-hero">
          <div className="hp-community-info__park-copy">
            <span className="hp-community-info__eyebrow">디에트르 라포레</span>
            <h1>커뮤니티 센터</h1>
            <p>아이의 놀이부터 나만의 운동과 휴식까지, 한 곳에</p>
          </div>
          <div className="hp-community-info__park-visual" aria-hidden="true">
            <img src={hpointAssets.community.centerHeroIllustration} alt="" />
          </div>
        </header>

        <section className="hp-community-info__audience-guide" aria-labelledby="audience-guide-title">
          <header className="hp-community-info__audience-heading">
            <span className="hp-community-info__eyebrow">공간 찾기</span>
            <h2 id="audience-guide-title">누구와 함께 오셨나요?</h2>
            <p>연령은 추천 기준이며, 실제 이용 가능 연령은 아래 시설별 이용 안내에서 확인해 주세요.</p>
          </header>
          <div className="hp-community-info__audience-filters" aria-label="추천 연령 선택">
            {audienceFilters.map((audience) => {
              const isActive = selectedAudience === audience;

              return (
                <button
                  key={audience}
                  type="button"
                  className={`hp-community-info__audience-filter${isActive ? " hp-community-info__audience-filter--active" : ""}`}
                  aria-pressed={isActive}
                  onClick={() => setSelectedAudience(audience)}
                >
                  {audience}
                </button>
              );
            })}
          </div>
          <p className="hp-community-info__audience-result" aria-live="polite" aria-atomic="true">
            {selectedAudience === "전체" ? "모든 연령" : selectedAudience} 추천 시설 {filteredDestinations.length}곳
          </p>
        </section>

        <section className="hp-community-info__discovery" aria-label="연령별 추천 시설 둘러보기">
          {discoveryZones.map((zone) => {
            const zoneDestinations = filteredDestinations.filter((destination) => destination.zone === zone.id);

            if (!zoneDestinations.length) {
              return null;
            }

            return (
              <section
                key={zone.id}
                className={`hp-community-info__zone hp-community-info__zone--${zone.id}`}
                aria-labelledby={`${zone.id}-zone-title`}
              >
                <header className="hp-community-info__zone-heading">
                  <span className="hp-community-info__zone-label">{zone.label}</span>
                  <h2 id={`${zone.id}-zone-title`}>{zone.title}</h2>
                  <p className="hp-community-info__zone-description">{zone.description}</p>
                </header>
                <div className="hp-community-info__destination-grid">
                  {zoneDestinations.map((destination) => (
                    <DestinationCard key={destination.name} destination={destination} />
                  ))}
                </div>
              </section>
            );
          })}
        </section>
      </section>

      <section className="hp-community-info__courses" aria-labelledby="course-title">
        <header className="hp-community-info__section-heading">
          <span className="hp-community-info__eyebrow">오늘의 추천 코스</span>
          <h2 id="course-title">이렇게 즐겨보세요</h2>
          <p>정해진 이용 순서가 아닌, 우리 단지에서 하루를 보내는 작은 아이디어예요.</p>
        </header>
        <div className="hp-community-info__course-grid">
          {recommendationCourses.map((course) => (
            <article className="hp-community-info__course-card" key={course.kicker} aria-label={course.kicker}>
              <span className="hp-community-info__course-kicker">{course.kicker}</span>
              <p>{course.description}</p>
              <ol className="hp-community-info__course-route" aria-label={`${course.kicker} 추천 순서`}>
                {course.route.map((stop) => (
                  <li key={stop}>{stop}</li>
                ))}
              </ol>
            </article>
          ))}
        </div>
      </section>

      <section className="hp-community-info__operations" aria-labelledby="operations-title">
        <header className="hp-community-info__operations-heading">
          <span className="hp-community-info__eyebrow">이용 전에 꼭 확인하세요</span>
          <h2 id="operations-title">운영시간·요금 안내</h2>
          <p>추천 대상과 실제 이용 기준은 다를 수 있으니 시설별 이용대상과 운영 규정을 확인해 주세요.</p>
        </header>

        <section className="hp-community-info__base-fee" aria-label="공통 이용 정보">
          <span className="hp-community-info__base-fee-icon" aria-hidden="true">
            <PaymentsRoundedIcon />
          </span>
          <div>
            <span>커뮤니티 기본 이용료</span>
            <strong>세대당 월 3,000원</strong>
          </div>
        </section>

        <div className="hp-community-info__quick-facts">
          <div>
            <AccessTimeRoundedIcon aria-hidden="true" />
            <span>운영 기준</span>
            <strong>연중무휴</strong>
          </div>
          <div>
            <PaymentsRoundedIcon aria-hidden="true" />
            <span>월 이용 신청</span>
            <strong>매월 25일 - 말일</strong>
          </div>
        </div>

        <ServiceSection
          id="paid-service-title"
          title="월 이용 · 예약 시설"
          services={paidServices}
        />

        <ServiceSection
          id="free-service-title"
          title="무료 이용 공간"
          description="별도 이용료 없이 운영시간에 맞춰 이용할 수 있는 공간이에요."
          services={freeServices}
        />

        <section className="hp-community-info__application-guide" aria-labelledby="application-guide-title">
          <span className="hp-community-info__application-guide-icon" aria-hidden="true">i</span>
          <div>
            <h2 id="application-guide-title">신청 전에 확인해 주세요</h2>
            <ul>
              <li>골프연습장·헬스장·독서실·락커 월 이용 신청은 매월 25일부터 말일까지 앱에서 가능합니다.</li>
              <li>신청 기간이 지나면 관리사무소에서 별도 신청서를 작성해야 하며, 이용료는 일할 계산되지 않습니다.</li>
              <li>이용 시작일 이후에는 취소할 수 없으니 신청 내용을 한 번 더 확인 바랍니다.</li>
              <li>당월 신청 내역은 익월 관리비에 부과됩니다.</li>
              <li>앱 신청 후 관리사무소에서 프로필을 확인하고 지문을 최초 1회 등록하면 시설 이용이 가능합니다.</li>
            </ul>
          </div>
        </section>
      </section>
    </main>
  );
};

export default CommunityInfo;
